import { PartnerPlatform } from '../../interfaces/PartnerPlatform/PartnerPlatform';
import { checkTrendyolStatus } from '../../api/Trendyol';
import { checkHepsiburadaStatus } from '../../api/Hepsiburada';
import { checkBoynerStatus } from '../../api/Boyner';
import { checkN11Status } from '../../api/N11';
import { checkPazaramaStatus } from '../../api/Pazarama';
import { availablePartnerPlatforms } from '../../constants/PartnerPlatform';
import { getProductMatchingsByPlatform } from '../../api/SynchronizationSummary';
import { getSettingsByType } from '../../api/Settings';

export function distributeVertically(
  startX: number,
  centerY: number,
  gap: number,
  count: number,
) {
  const totalHeight = (count - 1) * gap;
  const startY = centerY - totalHeight / 2;

  return Array.from({ length: count }).map((_, index) => ({
    x: startX,
    y: startY + index * gap,
  }));
}

const checkPartnerPlatformStatus = async (
  partnerPlatform: PartnerPlatform,
): Promise<boolean> => {
  try {
    switch (partnerPlatform) {
      case 'trendyol':
        await checkTrendyolStatus();
        break;
      case 'hepsiburada':
        await checkHepsiburadaStatus();
        break;
      case 'boyner':
        await checkBoynerStatus();
        break;
      case 'n11':
        await checkN11Status();
        break;
      case 'pazarama':
        await checkPazaramaStatus();
        break;
      default:
        break;
    }
  } catch {
    return false;
  }

  return true;
};

export const getPartnerPlatformStatuses = async (): Promise<
  Record<PartnerPlatform, boolean>
> => {
  return {
    trendyol: await checkPartnerPlatformStatus('trendyol'),
    hepsiburada: await checkPartnerPlatformStatus('hepsiburada'),
    boyner: await checkPartnerPlatformStatus('boyner'),
    n11: await checkPartnerPlatformStatus('n11'),
    pazarama: await checkPartnerPlatformStatus('pazarama'),
    internal: true,
  };
};

export const getPartnerPlatformMatchCounts = async (): Promise<
  Record<PartnerPlatform, number>
> => {
  const results = await Promise.all(
    availablePartnerPlatforms.map((platform) =>
      getProductMatchingsByPlatform({
        partnerPlatform: platform,
        identifier: '',
        pagination: { page: 0, pageSize: 1 },
      }),
    ),
  );

  const matchCounts: Record<PartnerPlatform, number> = {} as Record<
    PartnerPlatform,
    number
  >;

  availablePartnerPlatforms.forEach((platform, index) => {
    const result = results[index];
    matchCounts[platform] = result?.matchingCount ?? 0;
  });

  return matchCounts;
};

export const getPartnerPlatformEnablements = async (): Promise<
  Record<PartnerPlatform, boolean>
> => {
  const trendyolSettings = await getSettingsByType('trendyol');
  const hepsiburadaSettings = await getSettingsByType('hepsiburada');
  const boynerSettings = await getSettingsByType('boyner');
  const n11Settings = await getSettingsByType('n11');
  const pazaramaSettings = await getSettingsByType('pazarama');

  return {
    trendyol: !!trendyolSettings.find((x) => x.key === 'trendyol_status')
      ?.genericValue?.intValue,
    hepsiburada: !!hepsiburadaSettings.find(
      (x) => x.key === 'hepsiburada_status',
    )?.genericValue?.intValue,
    boyner: !!boynerSettings.find((x) => x.key === 'boyner_status')
      ?.genericValue?.intValue,
    n11: !!n11Settings.find((x) => x.key === 'n11_status')?.genericValue
      ?.intValue,
    pazarama: !!pazaramaSettings.find((x) => x.key === 'pazarama_status')
      ?.genericValue?.intValue,
    internal: true,
  };
};

export const getMatchingFieldName = (partnerPlatform: PartnerPlatform) =>
  partnerPlatform === 'trendyol' ||
  partnerPlatform === 'boyner' ||
  partnerPlatform === 'pazarama'
    ? 'Barkod'
    : partnerPlatform === 'hepsiburada'
    ? 'Hepsiburada SKU'
    : 'SKU';
